import { writable } from "svelte/store";
import LoadState from "../../Enums/LoadState";
import BaseModel from "../../Models/BaseModel";

const PortsWPI2019AndWPDMapping = {
	id: "properties.ID",
	index: "properties.INDEX_NO",
	region: "properties.REGION_NO",
	name: "properties.PORT_NAME",
	country: "properties.COUNTRY",
	portType: "properties.TYPE",
	portTypeReadable: "properties.TYPE_READABLE",
	locode: "properties.LOCODE",
	lng: "properties.LONGITUDE",
	lat: "properties.LATITUDE",
	source: "properties.SOURCE" // A = WPI, B = WPD.
};

class Port extends BaseModel {
	constructor(feature) {
		super(PortsWPI2019AndWPDMapping, Port.defaults);

		this.data = feature;
		this.data.properties.TYPE_READABLE = PortTypeMapping[this.portType];

		this.loadState = writable(LoadState.INIT);
		this.loadError = writable("");
		this.stats = null;
		this.statsRolling = null;
		this.visible = writable(true);
	}

	get name() {
		return this.get("name");
	}

	get coordinates() {
		return this.data.geometry.coordinates;
	}

	get nameAndTypeReadable() {
		return this.get("name") + " (" + this.portTypeReadable + ")";
	}

	get lng() {
		return this.get("lng");
	}

	get lat() {
		return this.get("lat");
	}

	get country() {
		return this.get("country");
	}

	get portType() {
		return this.get("portType");
	}

	get portTypeReadable() {
		return this.get("portTypeReadable");
		// return PortTypeMapping[this.portType];
	}

	// get esriId() {
	// 	return this.get("esriId");
	// }

	get locode() {
		return this.get("locode");
		// const SearchPorts = Ports;
		// if (this._locode === null) {
		// 	for (let i = 0; i < SearchPorts.data.features.length; i++) {
		// 		const feature = SearchPorts.data.features[i];
		// 		if (feature.properties.name.toLowerCase().search(this.name.toLowerCase()) !== -1) {
		// 			if (feature.properties.locode !== null &&
		// 				feature.properties.locode.toUpperCase().indexOf(this.country.toUpperCase()) === 0) {
		// 				this._locode = feature.properties.locode;
		// 			}
		// 		}
		// 	}
		// 	if (this._locode === null) {
		// 		this._locode = "";
		// 	}
		// }
		// return this._locode;
	}

	get uniqId() {
		return this.get("id");
		// return this.get("index");
	}

	get id() {
		return this.get("id");
	}

	asFeature() {
		return this.data;
	}
}

Port.defaults = { // Defaults
	id: -1,
	index: -1,
	name: "",
	lat: 0,
	lng: 0,
	country: "",
	portType: "P",
	portTypeReadable: "Port",
	source: "A"
};

// There must be 10000x better ways of doing this, but, until we need to do this same thing again... write once, refactor later.
const UNKNOWN_TERM = "Not Specified";
class FieldMap {
	constructor(key, enumMapping = null, defaultValue = UNKNOWN_TERM) {
		this.key = key;
		this.enumMapping = enumMapping;
		this.defaultValue = defaultValue;
	}

	transform(value) {
		if (this.enumMapping === null) {
			return (value === null || value === "")
				? this.defaultValue
				: value;
		}
		if (this.enumMapping[value]) {
			return this.enumMapping[value];
		}
		return (value === null || value === "")
			? this.defaultValue
			: value;
	}
}

// See: https://github.com/Geollect/WPI_WPD/blob/main/WPI_WPD_Merge.ipynb
const PortTypeMapping = {
	P: "Port",
	C: "Channel",
	S: "Shelter",
	A: "Anchorage",
	D: "Demolition Yard",
	F: "Fishing Harbour",
	O: "Offshore Terminal",
	M: "Marina/Small Harbour"
};
Port.PortTypeMapping = PortTypeMapping;

const YesNoMapping = {
	Y: "Yes",
	N: "No",
	"": UNKNOWN_TERM
};
const DataSourceMapping = {
	A: "WPI",
	B: "WPD"
};
const HarbourSizeMapping = {
	L: "Large",
	M: "Medium",
	S: "Small",
	V: "Very Small",
	"": UNKNOWN_TERM
};
const HarbourTypeMapping = {
	CN: "Coastal Natural",
	CB: "Coastal Breakwater",
	CT: "Coastal Tide Gate",
	RN: "River Natural",
	RB: "River Basin",
	RT: "River Tide Gate",
	LC: "Lake or Canal",
	OR: "Open Roadstead",
	TH: "Typhoon Harbour",
	N: "None",
	"": UNKNOWN_TERM
};
const ShelterMapping = {
	E: "Excellent",
	G: "Good",
	F: "Fair",
	P: "Poor",
	N: "None",
	"": UNKNOWN_TERM
};
const DepthMapping = {
	A: "76+feet; 23.2+ meters",
	B: "71-75 feet; 21.6-22.9 meters",
	C: "66-70 feet; 20.1-21.3 meters",
	D: "61-65 feet; 18.6-19.8 meters",
	E: "56-60 feet; 17.1-18.2 meters",
	F: "51-55 feet; 15.5-16.8 meters",
	G: "46-51 feet; 14.0-15.2 meters",
	H: "41-45 feet; 12.5-13.7 meters",
	J: "36-40 feet; 11.0-12.2 meters",
	K: "31-35 feet; 9.4-10.7 meters",
	L: "26-30 feet; 7.9-9.1 meters",
	M: "21-25 feet; 6.4-7.6 meters",
	N: "16-20 feet; 4.9-6.1 meters",
	O: "11-15 feet; 3.4-4.6 meters",
	P: "6-10 feet; 1.8-3.0 meters",
	Q: "0-5 feet; 0-1.5 meters",
	"": UNKNOWN_TERM
};
const MaxSizeMapping = {
	L: "Over 500' Length",
	M: "Up to 500' Length"
};
const RepairMapping = {
	A: "Major",
	B: "Moderate",
	C: "Limited",
	D: "Emergency Only",
	N: "None",
	"": UNKNOWN_TERM
};
const DrydockRailwayMapping = {
	L: "Large",
	M: "Medium",
	S: "Small",
	"": UNKNOWN_TERM
};
Port._FieldKeys = {
	"Index No.": new FieldMap(PortsWPI2019AndWPDMapping.index),
	"Region No.": new FieldMap(PortsWPI2019AndWPDMapping.region),
	"Port Name": new FieldMap(PortsWPI2019AndWPDMapping.name),
	"Port Type": new FieldMap("properties.TYPE", PortTypeMapping),
	"Country Code": new FieldMap(PortsWPI2019AndWPDMapping.country),
	Longitude: new FieldMap(PortsWPI2019AndWPDMapping.lng),
	Latitude: new FieldMap(PortsWPI2019AndWPDMapping.lat),
	"Publication Number (NGA Sailing Directions)": new FieldMap("properties.PUB"),
	"Chart Reference": new FieldMap("properties.CHART"),
	"Harbour Size": new FieldMap("properties.HARBORSIZE", HarbourSizeMapping),
	"Harbour Type": new FieldMap("properties.HARBORTYPE", HarbourTypeMapping),
	"Shelter Afforded": new FieldMap("properties.SHELTER", ShelterMapping),
	"Entrance Restrictions: Tide (Y/N)": new FieldMap("properties.ENTRY_TIDE", YesNoMapping),
	"Entrance Restrictions: Swell (Y/N)": new FieldMap("properties.ENTRYSWELL", YesNoMapping),
	"Entrance Restrictions: Ice (Y/N)": new FieldMap("properties.ENTRY_ICE", YesNoMapping),
	"Entrance Restrictions: Other (Y/N)": new FieldMap("properties.ENTRYOTHER", YesNoMapping),
	"Overhead Limit (Y/N)": new FieldMap("properties.OVERHD_LIM", YesNoMapping),
	"Channel Depth": new FieldMap("properties.CHAN_DEPTH", DepthMapping),
	"Anchorage Depth": new FieldMap("properties.ANCH_DEPTH", DepthMapping),
	"Cargo Pier Depth": new FieldMap("properties.CARGODEPTH", DepthMapping),
	"Oil Terminal Depth": new FieldMap("properties.OIL_DEPTH", DepthMapping),
	"Tide Mean Range (m)": new FieldMap("properties.TIDE_RANGE"),
	"Max Size Vessel": new FieldMap("properties.MAX_VESSEL", MaxSizeMapping),
	"Good Holding Ground (Y/N)": new FieldMap("properties.HOLDGROUND", YesNoMapping),
	"Turn Area (Y/N)":	new FieldMap("properties.TURN_BASIN", YesNoMapping),
	"1st Port Of Entry (Y/N)": new FieldMap("properties.PORTOFENTR", YesNoMapping),
	"U.S. Representative (Y/N)": new FieldMap("properties.US_REP", YesNoMapping),
	"ETA Message (Y/N)": new FieldMap("properties.ETAMESSAGE", YesNoMapping),
	"Pilotage: Required (Y/N)": new FieldMap("properties.PILOT_REQD", YesNoMapping),
	"Pilotage: Available (Y/N)": new FieldMap("properties.PILOTAVAIL", YesNoMapping),
	"Pilotage: Local Assist (Y/N)": new FieldMap("properties.LOC_ASSIST", YesNoMapping),
	"Pilotage: Advised (Y/N)": new FieldMap("properties.PILOTADVSD", YesNoMapping),
	"Tugs Salvage (Y/N)": new FieldMap("properties.TUGSALVAGE", YesNoMapping),
	"Tugs Assist (Y/N)": new FieldMap("properties.TUG_ASSIST", YesNoMapping),
	"Quarantine: Pratique (Y/N)": new FieldMap("properties.PRATIQUE", YesNoMapping),
	"Quarantine: SSCC Certification (Y/N)": new FieldMap("properties.SSCC_CERT", YesNoMapping),
	"Quarantine: Other (Y/N)": new FieldMap("properties.QUAR_OTHER", YesNoMapping),
	"Communications: Phone (Y/N)": new FieldMap("properties.COMM_PHONE", YesNoMapping),
	"Communications: Fax (Y/N)": new FieldMap("properties.COMM_FAX", YesNoMapping),
	"Communications: Radio (Y/N)": new FieldMap("properties.COMM_RADIO", YesNoMapping),
	"Communications: VHF (Y/N)": new FieldMap("properties.COMM_VHF", YesNoMapping),
	"Communications: Air (Y/N)": new FieldMap("properties.COMM_AIR", YesNoMapping),
	"Communications: Rail (Y/N)": new FieldMap("properties.COMM_RAIL", YesNoMapping),
	"Load/Offload: Wharf (Y/N)": new FieldMap("properties.CARGOWHARF", YesNoMapping),
	"Load/Offload: Anchorage (Y/N)": new FieldMap("properties.CARGO_ANCH", YesNoMapping),
	"Load/Offload: Med Moor (Y/N)": new FieldMap("properties.CARGMDMOOR", YesNoMapping),
	"Load/Offload: Beach Moor (Y/N)": new FieldMap("properties.CARBCHMOOR", YesNoMapping),
	"Load/Offload: Ice Moor (Y/N)": new FieldMap("properties.CARICEMOOR", YesNoMapping),
	"Medical Facilities (Y/N)": new FieldMap("properties.MED_FACIL", YesNoMapping),
	"Garbage Disposal (Y/N)": new FieldMap("properties.GARBAGE", YesNoMapping),
	"Degauss (Y/N)": new FieldMap("properties.DEGAUSS", YesNoMapping),
	"Dirty Ballast (Y/N)": new FieldMap("properties.DRTYBALLST", YesNoMapping),
	"Cranes: Fixed (Y/N)": new FieldMap("properties.CRANEFIXED", YesNoMapping),
	"Cranes: Mobile (Y/N)": new FieldMap("properties.CRANEMOBIL", YesNoMapping),
	"Cranes: Floating (Y/N)": new FieldMap("properties.CRANEFLOAT", YesNoMapping),
	"Lifts: 100 Ton Plus (Y/N)": new FieldMap("properties.LIFT_100_", YesNoMapping),
	"Lifts: 50-100 Tons (Y/N)": new FieldMap("properties.LIFT50_100", YesNoMapping),
	"Lifts: 25-49 Tons (Y/N)": new FieldMap("properties.LIFT_25_49", YesNoMapping),
	"Lifts: 0-24 Tons (Y/N)": new FieldMap("properties.LIFT_0_24", YesNoMapping),
	"Services: Longshore (Y/N)": new FieldMap("properties.LONGSHORE", YesNoMapping),
	"Services: Electrical (Y/N)": new FieldMap("properties.ELECTRICAL", YesNoMapping),
	"Services: Steam (Y/N)": new FieldMap("properties.SERV_STEAM", YesNoMapping),
	"Services: Navigation Equipment (Y/N)": new FieldMap("properties.NAV_EQUIP", YesNoMapping),
	"Services: Electrical Repair (Y/N)": new FieldMap("properties.ELECREPAIR", YesNoMapping),
	"Services: Provisions Services (Y/N)": new FieldMap("properties.PROVISIONS", YesNoMapping),
	"Supplies: Water (Y/N)": new FieldMap("properties.WATER", YesNoMapping),
	"Supplies: Fuel Oil (Y/N)": new FieldMap("properties.FUEL_OIL", YesNoMapping),
	"Supplies: Diesel (Y/N)": new FieldMap("properties.DIESEL", YesNoMapping),
	"Supplies: Deck (Y/N)": new FieldMap("properties.DECKSUPPLY", YesNoMapping),
	"Supplies: Engine (Y/N)": new FieldMap("properties.ENG_SUPPLY", YesNoMapping),
	Repair: new FieldMap("properties.REPAIRCODE", RepairMapping),
	"Dry Dock": new FieldMap("properties.DRYDOCK", DrydockRailwayMapping),
	Railway: new FieldMap("properties.RAILWAY", DrydockRailwayMapping),
	Source: new FieldMap("properties.SOURCE", DataSourceMapping)
};

Port._FieldKeysProps = {};
const fieldKeys = Object.keys(Port._FieldKeys);
for (let i = 0; i < fieldKeys.length; i++) {
	Port._FieldKeysProps[fieldKeys[i]] = Port._FieldKeys[fieldKeys[i]].key;
}

export default Port;
